import type { Edge, Node } from '@xyflow/react'
import {
  UniversalNodeRegistry,
  type UniversalNodeSchema,
} from '@/features/backtest/schemas/universal-node-schema'
import { cleanDataForSerialization } from '@/features/backtest/utils/data-cleaning-utils'
import { lowerEntryWindows, stripEntryWindowFields } from '@/features/backtest/utils/entry-window'
import { compoundLowerings } from '@/features/backtest/schemas/compound'
import { inlinePassthroughs } from '@/features/backtest/utils/inline-passthroughs'
import { isProxyEdge, type Lowering } from '@/features/backtest/utils/graph-lowering'
import {
  pruneDisabledStreams,
  stripEditorOnlyNodes,
} from '@/features/backtest/utils/prune-disabled-streams'
import { strategyTickerDay, withRunTickerDay } from '@/features/backtest/utils/run-ticker-day'
import { followWire } from '@/features/backtest/utils/static-mode'

type GraphRecord = Record<string, unknown>
type EngineGraph = { nodes: GraphRecord[]; edges: GraphRecord[] }

export interface EngineView extends EngineGraph {
  /** The canvas node an engine id belongs to (see `toEngineView`). */
  ownerOf: (id: string) => string
}

export interface EngineViewOptions {
  /**
   * Splice out the pass-through pills (reroutes, subnet I/O) and drop the
   * nodes with no schema (subnet and group frames, stickies), as the engine
   * does on its side. Readiness and the EdgeScript printer read the graph this
   * way; the payload leaves it to the engine.
   */
  inline?: boolean
  /**
   * Editor-only types to keep. The EdgeScript printer keeps the Universe: a
   * strategy script names its universe (`universe "<name>"`).
   */
  keep?: ReadonlySet<string>
  /**
   * Serialize as a save does (default). The printer reads the editor's nodes
   * as they are: its serializer (graphToRecipe) knows a time-slice preset by
   * its `timeSlice:<preset>` type, which serialization folds into `presetId`.
   */
  serialize?: boolean
}

/**
 * The graph the engine runs, from the editor graph (compound audit step 4,
 * 2026-10-06): the one pipeline the run payload (single run, preview, CSV
 * batch, debug run, headless run) and readiness read, so they cannot drift.
 *
 *   0. canonicalize: drop a collapsed subnet's proxy wires, and set each
 *      input that follows its wire (a Condition's B) by its wiring, so a
 *      stale stored Node / Static cannot mislead the run (static-mode.ts);
 *   1. lower: each compound's declared lowering, then each facet's (a Trade
 *      Manager into the Advanced Trade cluster it stands for, then each entry
 *      window into its Time Signal and Merge);
 *   2. serialize the editor graph (as a save does), unless told not to;
 *   3. prune the streams that only feed a switched-off And/Or input;
 *   4. strip the editor-only nodes (the Report, the Universe);
 *   5. take the window fields off the entry nodes;
 *   6. take canvas styling off every node;
 *   7. with `inline`, splice the pills out and drop the schema-less nodes.
 *
 * `ownerOf` maps an id a lowering generated to the node that made it, through
 * both lowerings (a Trade Manager's window Time Signal belongs to its
 * generated Entry Rules, which belongs to the Trade Manager); any other id is
 * its own owner. Readiness and run errors point at the node it names.
 */
export function toEngineView(
  nodes: Node[],
  edges: Edge[],
  options: EngineViewOptions = {},
): EngineView {
  // A collapsed subnet's proxy wires are the canvas's drawing of its
  // canonical ones; every stage reads the canonical graph.
  const canonical = edges.filter((e) => !isProxyEdge(e))
  // Before serializing: its reconcile makes the entry follow the setting.
  const followed = nodes.map((node) => followWire(node, canonical))
  const lowered = lower(followed, canonical)
  // Last pass first: a Trade Manager's window Time Signal belongs to its
  // generated Entry Rules, which belongs to the Trade Manager.
  const ownerOf = (id: string): string =>
    lowered.owners.reduceRight((owner, owners) => owners.get(owner) ?? owner, id)
  const serialized =
    options.serialize === false
      ? (lowered as unknown as EngineGraph)
      : cleanDataForSerialization(lowered.nodes, lowered.edges)
  const pruned = pruneDisabledStreams(serialized, lowered.nodes, lowered.edges)
  const graph = withoutCanvasStyling(
    stripEntryWindowFields(stripEditorOnlyNodes(pruned, options.keep)),
  )
  return { ...(options.inline ? inlined(graph) : graph), ownerOf }
}

/**
 * The facets' lowerings, run after every compound's. Keyed by the facet, so a
 * facet added to `UniversalNodeSchema.facets` fails to compile until its
 * lowering is named here.
 */
const FACET_LOWERINGS: Record<keyof NonNullable<UniversalNodeSchema['facets']>, Lowering> = {
  entryWindow: lowerEntryWindows,
}

/**
 * Stage 1: each compound's declared lowering in declared order (a Trade
 * Manager into the Advanced Trade cluster), then each facet's (an entry window
 * into its Time Signal and Merge; it runs last because a Trade Manager's
 * window moves to the Entry Rules it generates). Every pass returns the ids it
 * claimed, kept in pass order for `ownerOf`.
 */
function lower(nodes: Node[], edges: Edge[]) {
  const passes: Lowering[] = [
    ...compoundLowerings().map((l) => l.lower),
    ...Object.values(FACET_LOWERINGS),
  ]
  let graph = { nodes, edges }
  const owners: Array<Map<string, string>> = []
  for (const pass of passes) {
    const out = pass(graph.nodes, graph.edges)
    graph = { nodes: out.nodes, edges: out.edges }
    owners.push(out.owners)
  }
  return { ...graph, owners }
}

/**
 * Canvas styling stays on the canvas. The engine's node model requires a
 * zIndex inside `style`, and subnet frames store only their size (Create
 * Subnet dropped the stored zIndex for the z-band system), so a subnet in the
 * payload failed every run with "Invalid value for 'style'" (2026-10-06).
 */
function withoutCanvasStyling(graph: EngineGraph): EngineGraph {
  return {
    ...graph,
    nodes: graph.nodes.map((node) => {
      if (!('style' in node)) return node
      const { style: _style, ...rest } = node
      return rest
    }),
  }
}

type InlineNode = GraphRecord & { id: string; type?: string }
type InlineEdge = GraphRecord & {
  id: string
  source: string
  target: string
  sourceHandle?: string | null
  targetHandle?: string | null
}

/**
 * Pass-through pills carry no schema, so they are SPLICED out (each one's
 * upstream source reconnected to its downstream consumers, source handle
 * kept, chains collapsed), the identity inlining the engine applies; dropping
 * them instead cut every wire routed through a pill. Then only nodes the
 * registry resolves to a schema stay, which drops frames and stickies rather
 * than reporting them as unknown, with any edge left dangling.
 */
function inlined(graph: EngineGraph): EngineGraph {
  const spliced = inlinePassthroughs(graph.nodes as InlineNode[], graph.edges as InlineEdge[])
  const known = spliced.nodes.filter(
    (n) => UniversalNodeRegistry.getSchemaResolved(n.type as string) !== undefined,
  )
  const knownIds = new Set(known.map((n) => n.id))
  return {
    nodes: known,
    edges: spliced.edges.filter((e) => knownIds.has(e.source) && knownIds.has(e.target)),
  }
}

/**
 * The engine payload plus `ownerOf` (see `toEngineView`), with the send step:
 * every Price Data on the run's ticker and day (`strategyTickerDay`: the
 * connected Stock Selection's, else the main Price Data's), Reference Data on
 * the day. The Stock Selection owns them since 2026-10-28, but the engine still
 * reads them off Price Data, so its copy carries them. Read from the editor
 * graph, which still holds the Stock Selection; written on the payload's
 * records. Only a send does this: readiness and the EdgeScript printer read
 * `toEngineView` as built. When the engine reads the Stock Selection itself,
 * delete this step.
 *
 * Send-time only, not save-time (checked 2026-10-28): nothing reads Price
 * Data's ticker or day off the saved document. RetroStock's single run and
 * batch take them from the request (`ticker`/`target_date`) and run the graph
 * the request carries (a batch's per-ticker runs, its snapshot), and the AI's
 * saved-strategy run reads the document in the browser and sends it through
 * here.
 */
export function buildEngineView(nodes: Node[], edges: Edge[]): EngineView {
  const view = toEngineView(nodes, edges)
  const day = strategyTickerDay(nodes, edges)
  return { ...view, nodes: withRunTickerDay(view.nodes as InlineNode[], day) }
}

/**
 * The engine payload: `{ nodes, edges }` only. EVERY engine payload (single
 * run, preview, CSV batch, debug run) builds its `strategy.{nodes,edges}`
 * through this so the paths can't drift; batch once skipped the prune and
 * shipped dead gates raw, which the engine rejects ("And/Or gate has no
 * enabled inputs").
 */
export function buildEngineStrategyGraph(nodes: Node[], edges: Edge[]): EngineGraph {
  const { nodes: engineNodes, edges: engineEdges } = buildEngineView(nodes, edges)
  return { nodes: engineNodes, edges: engineEdges }
}
